import React from 'react';
import { View, Image, Pressable } from 'react-native';
import { DeviceTheme, GetTransactionListTransactionDto, GetTransactionListTransactionType } from '../../../api/api.g';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import Label, { LabelType } from '../../common/Label';
import { useAppSelector } from '../../../hooks/store-hook';
import { StylesHelper } from '../../../models/helpers/stylesHelper';
import { formatTransactionDate, getTransactionDateKey } from '../../../helpers/transactionDate';
import { formatAdjustmentReason } from '../../../helpers/transactionReason';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import {
  faCheckDouble,
  faCirclePlus,
  faDash,
  faGift,
  faLocationDot,
  faMapLocationDot,
  faMoneyCheckDollarPen,
  faQuestion,
  faSackDollar,
} from '@fortawesome/pro-regular-svg-icons';
import useMyAccountActions from '../../../hooks/myAccountActions-hook';
import { rootNavigate } from '../../../navigation/rootNavigation';

export interface MyTransactionsItemProps {
  transactions: GetTransactionListTransactionDto[];
}

const MyTransactionsItem = (props: MyTransactionsItemProps) => {
  const theme = useCustomTheme();
  const t = useTranslation();
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const myAccountActions = useMyAccountActions();
  const isDarkMode = userPrefs.theme === DeviceTheme.Dark;

  const transactions = props.transactions;

  const getTransactionItem = (transaction: GetTransactionListTransactionDto) => {
    const innerContent = (
      <View testID="transaction-row" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, minHeight: 40 }}>
        <View testID="transaction-icon" style={{ width: 28, height: 28, flexShrink: 0, alignItems: 'center', justifyContent: 'center' }}>
          {getTransactionIcon(transaction)}
        </View>
        <View testID="transaction-content" style={{ flex: 1, minWidth: 0, gap: 4 }}>
          <View testID="transaction-header" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
            <Label
              variant={LabelType.titleLargeNoCaps}
              numberOfLines={0}
              style={{ flex: 1, minWidth: 0, fontSize: 14, lineHeight: 20, fontWeight: '600' }}
            >
              {getTransactionTitle(transaction)}
            </Label>
            <View
              testID="transaction-amount"
              style={{ minWidth: 88, flexShrink: 0, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}
            >
              <Label
                variant={LabelType.titleLargeNoCaps}
                numberOfLines={1}
                style={{ flexShrink: 0, fontSize: 15, lineHeight: 20, fontWeight: '500', textAlign: 'right', fontVariant: ['tabular-nums'], color: getColor(transaction.amountPoints) }}
              >
                {t('points-value', { value: transaction.amountPoints })}
              </Label>
              <View style={{ width: 16, height: 20, flexShrink: 0, alignItems: 'center', justifyContent: 'center' }}>
                <FontAwesomeIcon {...getArrow(transaction.amountPoints)} size={16} />
              </View>
            </View>
          </View>
          {getSecondLine(transaction)}
        </View>
      </View>
    );

    return transaction.type === GetTransactionListTransactionType.Mission ? (
      <Pressable
        onPress={() => {
          rootNavigate('mymissions', { id: transaction.missionAssignationId });
          myAccountActions.setVisibility(false);
        }}
      >
        {innerContent}
      </Pressable>
    ) : (
      innerContent
    );
  };

  const getTransactionIcon = (transaction: GetTransactionListTransactionDto) => {
    const transactionIconColor = (isDarkMode ? '#ffffff' : '#000000') as any;
    const whiteBadgeBackground = isDarkMode ? 'transparent' : 'white';

    switch (transaction.type) {
      case GetTransactionListTransactionType.Mission:
        return transaction.hasLogo ? (
          <Image style={{ width: 28, height: 28, ...StylesHelper.borderRadius(14) }} source={{ uri: transaction.logoUrl }} />
        ) : (
          <FontAwesomeIcon icon={getIcon(transaction.type)} color={transactionIconColor} size={24} />
        );
      case GetTransactionListTransactionType.CashOut:
        return (
          <View
            style={{
              width: 28,
              height: 28,
              ...StylesHelper.borderRadius(14),
              backgroundColor: whiteBadgeBackground,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FontAwesomeIcon icon={getIcon(transaction.type)} color={transactionIconColor} size={16} />
          </View>
        );
      case GetTransactionListTransactionType.Adjustment:
        return <FontAwesomeIcon icon={getIcon(transaction.type)} color={transactionIconColor} size={24} />;
      default:
        return (
          <View
            style={{
              width: 28,
              height: 28,
              ...StylesHelper.borderRadius(14),
              backgroundColor: whiteBadgeBackground,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FontAwesomeIcon icon={getIcon(transaction.type)} color={transactionIconColor} size={24} />
          </View>
        );
    }
  };

  const getTransactionTitle = (transaction: GetTransactionListTransactionDto) => {
    switch (transaction.type) {
      case GetTransactionListTransactionType.Mission:
        return transaction.missionConfigurationName;
      case GetTransactionListTransactionType.MissionReferral:
        return t('transaction-type.mission-referral');
      case GetTransactionListTransactionType.Adjustment:
        return t('transaction-type.adjustment');
      case GetTransactionListTransactionType.CashOut:
        return t('transaction-type.cashout');
      case GetTransactionListTransactionType.Validation:
        return t('transaction-type.validation');
      default:
        return t('transaction-type-unknown');
    }
  };

  const getSecondLine = (transaction: GetTransactionListTransactionDto) => {
    const locationLabel = [transaction.name ? t(transaction.name) : undefined, transaction.branchAddress].filter(Boolean).join(' - ');
    const hasLocation = transaction.type === GetTransactionListTransactionType.Mission && !!transaction.branchId && !!locationLabel;
    const canOpenMap = hasLocation && !!transaction.branchGeocoordinate;
    const hasReferralName = transaction.type === GetTransactionListTransactionType.MissionReferral && !!transaction.missionConfigurationName;
    if (!hasLocation && !hasReferralName && !transaction.reason) return null;

    return (
      <View testID="transaction-details" style={{ minWidth: 0, gap: 4 }}>
        {hasLocation && (
          <Pressable
            disabled={!canOpenMap}
            style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 6 }}
            onPress={(event) => {
              event.stopPropagation();
              if (!canOpenMap) return;
              rootNavigate('map', { pos: transaction.branchId, location: `${transaction.branchGeocoordinate.y}|${transaction.branchGeocoordinate.x}` });
              myAccountActions.setVisibility(false);
            }}
          >
            <Label
              numberOfLines={0}
              style={{ flexShrink: 1, minWidth: 0, fontSize: 12, lineHeight: 16, ...(isDarkMode ? { color: '#e5edf4' as any } : {}) }}
              variant={isDarkMode ? LabelType.captionPrimary : LabelType.caption}
            >
              {locationLabel}
            </Label>
            {canOpenMap && (
              <View style={{ width: 12, height: 16, flexShrink: 0, alignItems: 'center', justifyContent: 'center' }}>
                <FontAwesomeIcon icon={faMapLocationDot} color={(isDarkMode ? '#ffffff' : '#000000') as any} size={12} />
              </View>
            )}
          </Pressable>
        )}
        {hasReferralName && (
          <Label
            numberOfLines={0}
            style={{ fontSize: 12, lineHeight: 16, ...(isDarkMode ? { color: '#e5edf4' as any } : {}) }}
            variant={isDarkMode ? LabelType.captionPrimary : LabelType.caption}
          >
            {transaction.missionConfigurationName}
          </Label>
        )}
        {transaction.reason && (
          <Label
            numberOfLines={0}
            style={{ fontSize: 12, lineHeight: 16, ...(isDarkMode ? { color: '#e5edf4' as any } : {}) }}
            variant={isDarkMode ? LabelType.captionPrimary : LabelType.caption}
          >
            {transaction.type === GetTransactionListTransactionType.Adjustment
              ? formatAdjustmentReason(transaction.reason, t)
              : t(transaction.reason)}
          </Label>
        )}
      </View>
    );
  };

  const getDateComponent = (showDate: boolean, date: Date) => {
    if (showDate) {
      return (
        <Label
          variant={LabelType.titleDarkSmall}
          style={{
            ...(isDarkMode ? { color: '#9adfd6' as any } : {}),
            paddingBottom: 8,
            marginBottom: 10,
            borderBottomColor: theme.borderColorLight,
            borderBottomWidth: 1,
          }}
        >
          {formatTransactionDate(date, userPrefs.uiLanguage)}
        </Label>
      );
    } else {
      return <></>;
    }
  };

  const getIcon = (type: GetTransactionListTransactionType) => {
    switch (type) {
      case GetTransactionListTransactionType.CashOut:
        return faSackDollar;
      case GetTransactionListTransactionType.Mission:
        return faLocationDot;
      case GetTransactionListTransactionType.MissionReferral:
        return faGift;
      case GetTransactionListTransactionType.Validation:
        return faCheckDouble;
      case GetTransactionListTransactionType.Adjustment:
        return faMoneyCheckDollarPen;
      default:
        return faQuestion;
    }
  };

  const getColor = (amount: number) => {
    if (amount < 0) {
      return isDarkMode ? '#ff6b9a' : theme.colorErrorPrimary;
    } else {
      if (amount > 0) {
        return isDarkMode ? '#78d8ca' : theme.text.titleLight;
      } else {
        return theme.text.primary;
      }
    }
  };

  const getArrow = (amount: number) => {
    const transactionIconColor = (isDarkMode ? '#ffffff' : '#000000') as any;

    if (amount < 0) {
      return { icon: faSackDollar, color: transactionIconColor };
    } else {
      if (amount > 0) {
        return { icon: faCirclePlus, color: transactionIconColor };
      } else {
        return { icon: faDash, color: transactionIconColor };
      }
    }
  };

  return (
    <View>
      {transactions.map((transaction, i) => (
        <View
          key={transaction.id}
          testID="transaction-card"
          style={{
            backgroundColor: isDarkMode ? theme.background.missionDetailSummary : theme.background.missionDetail,
            ...StylesHelper.padding(12),
            ...StylesHelper.borderRadius(5),
            marginBottom: 8,
          }}
        >
          {getDateComponent(
            i === 0 || getTransactionDateKey(transactions[i - 1].date) !== getTransactionDateKey(transaction.date),
            transaction.date,
          )}
          {getTransactionItem(transaction)}
        </View>
      ))}
    </View>
  );
};

export default MyTransactionsItem;
